'use client';

import { useEffect, useRef } from 'react';
import { useBackButtonFriction } from './useBackButtonFriction';
import { MockAppUI, resetMockAppAnalytics } from './MockAppUI';
import { ExitBottomSheet } from './ExitBottomSheet';

/**
 * Drop this as a sibling next to landing page content inside RouteClientBoundary.
 * It portals MockAppUI / ExitBottomSheet to document.body — escaping any
 * overflow-hidden container (e.g. V3SubscriptionContent) without wrapping children.
 *
 * State machine:
 *   landing  → (back press)       → mock-app
 *   mock-app → (thumbnail click)  → landing
 *   mock-app → (back press)       → exit-sheet
 *   exit-sheet → "No Thanks"      → history.back() (leaves the app)
 *   exit-sheet → "Start Trial"    → landing
 */
export function BackButtonFrictionOverlay() {
  const {
    frictionState,
    returnToLanding,
    exitApp
  } = useBackButtonFriction();
  const prevState = useRef(frictionState);

  // Reset analytics dedup state synchronously during render — before MockAppUI mounts
  // and any child effects fire. Must NOT be in useEffect because parent effects run
  // after child effects, which would cause impressionsSeen to be cleared after the
  // SpotlightCarousel already fires its first impression (producing a duplicate).
  if (prevState.current === 'landing' && frictionState === 'mock-app') {
    resetMockAppAnalytics();
  }
  useEffect(() => {
    // Notify subscription page that user has returned from MockAppUI
    if ((prevState.current === 'mock-app' || prevState.current === 'exit-sheet') && frictionState === 'landing') {
      window.dispatchEvent(new CustomEvent('alright-screen-returned'));
    }
    prevState.current = frictionState;
  }, [frictionState]);
  if (frictionState === 'landing') return null;
  if (frictionState === 'mock-app') {
    return <MockAppUI onThumbnailClick={returnToLanding} />;
  }

  // exit-sheet: MockAppUI stays visible in the background, ExitBottomSheet slides up on top
  return <>
      <MockAppUI onThumbnailClick={returnToLanding} />
      <ExitBottomSheet onNoThanks={exitApp} onStartTrial={returnToLanding} />
    </>;
}